import type { AccountInfo, IPublicClientApplication, PopupRequest, SilentRequest } from '@azure/msal-browser';
import { InteractionRequiredAuthError, createNestablePublicClientApplication } from '@azure/msal-browser';

import type { GetAuthTokenCache, YammerCache } from '../cache';
import { getTokenCacheTimeToLive } from '../cache';
import { AcquireTokenAuthError, AcquireTokenPopupError } from '../errors';
import type { Authenticator, AuthenticatorOptions } from '../types';
import { AuthenticatorType } from '../types';

type AccountFilter = Pick<
  Parameters<IPublicClientApplication['getAccount']>[0],
  'tenantId' | 'homeAccountId' | 'loginHint'
>;

export interface NestedAppAuthenticatorOptions extends Pick<
  AuthenticatorOptions,
  'instance' | 'clientId' | 'tenant' | 'onError'
> {
  readonly getAuthTokenCache: GetAuthTokenCache;
  readonly accountFilter: AccountFilter;
  readonly shouldAutoRefresh?: boolean;
}

/*
If your app is an iframed Nested app and delegating its authentication to a MetaOS SDK, you can use this authenticator to acquire tokens for the nested app.
*/
export default class NestedAppAuthenticator implements Authenticator {
  public readonly type: AuthenticatorType = AuthenticatorType.NestedAppAuth;
  private readonly authTokenCache: YammerCache | undefined;
  private readonly instance: string;
  private readonly clientId: string;
  private readonly tenant: string;
  private readonly accountFilter: AccountFilter;
  private readonly onError: AuthenticatorOptions['onError'];
  private nestablePca!: IPublicClientApplication;
  private readonly shouldAutoRefresh?: boolean;

  constructor({
    getAuthTokenCache,
    accountFilter,
    instance,
    clientId,
    tenant,
    onError,
    shouldAutoRefresh = true,
  }: NestedAppAuthenticatorOptions) {
    this.authTokenCache = getAuthTokenCache();
    this.accountFilter = accountFilter;
    this.instance = instance;
    this.clientId = clientId;
    this.tenant = tenant;
    this.onError = onError;
    this.shouldAutoRefresh = shouldAutoRefresh;
  }

  private async initializeNestablePca(): Promise<void> {
    try {
      this.nestablePca = await createNestablePublicClientApplication({
        auth: {
          clientId: this.clientId,
          authority: `${this.instance}${this.tenant}`,
        },
      });
    } catch (error) {
      await this.reportError(new Error('Error initializing nestable PCA: ' + (error as Error).message));
      throw error;
    }
  }

  private async getActiveAccount(): Promise<AccountInfo | undefined> {
    if (!this.nestablePca) {
      await this.initializeNestablePca();
    }

    let activeAccount;

    try {
      activeAccount = await this.nestablePca.getActiveAccount();
    } catch (error) {
      await this.reportError(new Error('Error getting active account: ' + (error as Error).message));
    }

    if (!activeAccount) {
      try {
        const accountWithFilter = this.nestablePca.getAccount(this.accountFilter);
        if (accountWithFilter) {
          activeAccount = accountWithFilter;
          this.nestablePca.setActiveAccount(activeAccount);
        }
      } catch (error) {
        await this.reportError(new Error('Error getting account with filter: ' + (error as Error).message));
        throw error;
      }
    }

    return activeAccount ?? undefined;
  }

  private async getTokenPopup(popupRequest: PopupRequest): Promise<string> {
    try {
      const { accessToken } = await this.nestablePca.acquireTokenPopup(popupRequest);

      return accessToken;
    } catch (error) {
      await this.reportError(new AcquireTokenPopupError((error as Error).message));
      throw new AcquireTokenPopupError((error as Error).message);
    }
  }

  private async getTokenSilent(resource: string, claims?: string, tenantId?: string): Promise<string> {
    const activeAccount = await this.getActiveAccount();

    const account =
      tenantId && activeAccount
        ? {
            ...activeAccount,
            tenantId,
          }
        : activeAccount;

    const authority = tenantId ? { authority: `${this.instance}${tenantId}` } : {};
    const silentRequestClaims = claims ? { claims } : {};

    const silentRequest: SilentRequest = {
      account,
      scopes: [resource],
      ...authority,
      ...silentRequestClaims,
    };

    try {
      const { accessToken } = await this.nestablePca.acquireTokenSilent(silentRequest);

      return accessToken;
    } catch (error) {
      await this.reportError(
        new AcquireTokenAuthError({ errorCode: 'AuthAcquireTokenFailed', errorMessage: (error as Error).message }),
        {
          resource,
        }
      );

      if (error instanceof InteractionRequiredAuthError) {
        await this.getTokenPopup(silentRequest).then((token) => {
          if (token && this.shouldAutoRefresh) {
            window.location.reload();
          }
        });
      }

      throw new AcquireTokenAuthError({ errorCode: 'AuthAcquireTokenFailed', errorMessage: (error as Error).message });
    }
  }

  private addTokenToCache = async (resource: string, token: string): Promise<void> => {
    const ttl = getTokenCacheTimeToLive(token);
    if (ttl && this.authTokenCache) {
      await this.authTokenCache.setItem(resource, token, ttl);
    }
  };

  private getTokenFromCache = async (resource: string): Promise<string | null> => {
    if (this.authTokenCache) {
      return await this.authTokenCache.getItem<string>(resource);
    }

    return null;
  };

  private async reportError(error: unknown, properties: { readonly resource?: string } = {}) {
    // istanbul ignore else
    if (this.onError) {
      try {
        await this.onError(error, { authenticatorType: this.type, ...properties });
      } catch (err) {
        console.error(err);
      }
    }
  }

  public getToken(resource: string, claims?: string, tenantId?: string): Promise<string> {
    return this.getTokenSilent(resource, claims, tenantId);
  }

  public acquireToken = async ([resource]: string[], claims?: string, tenantId?: string): Promise<string> => {
    if (!claims && !tenantId) {
      const cachedToken = await this.getTokenFromCache(resource);
      if (cachedToken) {
        return cachedToken;
      }
    }

    const token = await this.getToken(resource, claims, tenantId);

    if (!claims && !tenantId) {
      await this.addTokenToCache(resource, token);
    }

    return token;
  };

  public acquireTokenRedirect(): void {
    throw new Error('acquireTokenRedirect is not implemented');
  }

  public setThirdPartyCookieErrorCallback(): void {
    throw new Error('setThirdPartyCookieErrorCallback is not implemented');
  }
}
